import {
  useCommendaOSList,
  useCommendaOSRead,
} from '@commenda/api/commenda-os-dsl';
import { useBusinessEntity } from '@commenda/api/business-entity';
import { useApiScope } from '@commenda/api/scope';
import { InputErrorMessage } from '@commenda/kakashi';
import type { ValidationRule } from '@commenda/types';
import type {
  CorporationType,
  Country,
  GovernanceRole,
  StakeholderType,
} from '@prisma/client';
import { useCallback, useContext, useEffect, useRef, useState } from 'react';
import { getDefaultCurrency } from '../../config/country-bank-utils';
import { FormContext } from '../../hooks/FormContext';
import { generateId } from '../../types';
import { numOrUndef } from '../../utils/context-values';
import { isRuleActive } from '../../utils/validation';
import { AddOwnerModal } from './Ownership/AddOwnerModal';
import {
  type MembershipConfig,
  type PartnershipConfig,
  type SharesConfig,
  computeMembership,
  computePartnership,
  computeShares,
  hasZeroPercentOwner,
} from './Ownership/compute';
import {
  AUTHORIZED_SHARES_DESCRIPTION_DEFAULT,
  INCOMPLETE_ENTITY_DETAILS_ERROR,
  MIN_PARTNERS,
  MIN_PARTNERS_ERROR,
  hasIncompleteEntityDetails,
  noOwnersError,
  structureHasGeneralPartner,
  zeroPercentOwnerError,
} from './Ownership/copy';
import { MembershipSections } from './Ownership/MembershipSections';
import { MembershipSummary } from './Ownership/MembershipSummary';
import { MembershipTable } from './Ownership/MembershipTable';
import { PartnershipSections } from './Ownership/PartnershipSections';
import { PartnershipSummary } from './Ownership/PartnershipSummary';
import { PartnershipTable } from './Ownership/PartnershipTable';
import { PERSON_LABELS_KEY } from '../../dsl-constants';
import { OwnerSubFormDrawer } from './Ownership/OwnerSubFormDrawer';
import {
  reconcileMembership,
  reconcilePartnership,
  reconcileShareholdings,
} from './Ownership/persist';
import { SharesSections } from './Ownership/SharesSections';
import { SharesSummary } from './Ownership/SharesSummary';
import { SharesTable } from './Ownership/SharesTable';
import {
  type OwnershipEntry,
  type OwnershipFieldSettings,
  type OwnershipFieldValue,
  type OwnershipFieldVisibility,
  type OwnershipVariant,
  type PartnershipStructure,
  type StagedOwner,
} from './Ownership/types';

const boolOrUndef = (v: unknown): boolean | undefined =>
  typeof v === 'boolean' ? v : undefined;

interface OwnershipComponentProps {
  fieldId?: string;
  label?: string;
  description?: string;
  // Standard component validation (the form-builder's per-field "Required"
  // toggle lives at `validation.rules.required`). When required is active the
  // node's visible fields show a red dot and block submit.
  validation?: { rules?: { required?: ValidationRule | boolean } };
  props?: {
    variant?: OwnershipVariant;
    fields?: Record<
      string,
      { default?: unknown; visible?: boolean; editable?: boolean }
    >;
    jurisdiction?: { minShareCapital?: number; helperText?: string };
    shareCapitalEnabled?: boolean;
    holdingCountry?: string;
    partnershipStructure?: PartnershipStructure;
    copy?: { authorizedSharesDescription?: string };
    // FormTemplateKey collecting the rest of an entity owner's file —
    // registration number, registered address, incorporation documents. Set it
    // and every entity row must complete it before the form can be submitted.
    entityDetailsKeyId?: string;
    // FormTemplateKey used when adding a person who isn't on file yet, so the
    // cap table creates the same full record the People step does instead of a
    // name-and-nationality stub.
    personDetailsKeyId?: string;
    partnersFromFieldId?: string;
  };
}

// Shape read from corporation.ownership for prefill: the structural config from
// the first share class, plus the existing shareholdings used to rebuild owner
// rows. Field names follow the DSL read serializer; verify against it locally.
interface OwnershipPrefillRead {
  shareClasses?: Array<{
    authorizedShares?: number | null;
    pricePerShareAmount?: number | null;
    outstandingShares?: number | null;
    // Membership only — true ⇒ units were issued explicitly (price = capital ÷ units).
    hasIssuedUnits?: boolean | null;
  }>;
  // Existing owners — rebuilt into staged rows (identity from the nested
  // stakeholder, outstanding % from numberOfShares ÷ class outstandingShares).
  shareholdings?: Array<{
    // The stakeholding's id on the shareholding DSL node (used to delete removed owners).
    id?: string | null;
    shareholderDisplayName?: string | null;
    numberOfShares?: number | null;
    governanceRole?: GovernanceRole | null;
    isGeneralPartner?: boolean | null;
    shareholder?: {
      type?: StakeholderType | null;
      directorId?: number | null;
      corporationId?: number | null;
      nationality?: Country | null;
      director?: {
        directorFirstName?: string | null;
        directorLastName?: string | null;
        dateOfBirth?: string | null;
      } | null;
      corporation?: {
        companyLegalName?: string | null;
        incorporationCountry?: Country | null;
        jurisdiction?: string | null;
        corporationType?: CorporationType | null;
      } | null;
    } | null;
  }>;
}

// Owners the node created from an earlier people field, so a later reconcile can
// tell them from owners added here by hand.
const SEEDED_OWNER_PREFIX = 'person:';

function readOwnershipValue(raw: unknown): OwnershipFieldValue {
  if (raw && typeof raw === 'object' && !Array.isArray(raw)) {
    const v = raw as Partial<OwnershipFieldValue>;
    return {
      selectedIds: v.selectedIds ?? [],
      entries: v.entries ?? {},
      currency: v.currency,
      country: v.country,
      partnershipStructure: v.partnershipStructure,
      shares: v.shares,
      membership: v.membership,
      partnership: v.partnership,
    };
  }
  return { selectedIds: [], entries: {} };
}

// Rebuild staged owner rows from the corporation's existing shareholdings so the
// node prefills who already owns the entity. Outstanding % is reconstructed from
// each holding's share count over the class's issued total. Owners keep their
// existing directorId / corporationId so a re-submit upserts (not duplicates).
function mapPrefilledOwners(
  shareholdings: NonNullable<OwnershipPrefillRead['shareholdings']>,
  issued: number | undefined,
  gpApplies: boolean
): { selectedIds: string[]; entries: Record<string, OwnershipEntry> } {
  const selectedIds: string[] = [];
  const entries: Record<string, OwnershipEntry> = {};
  shareholdings.forEach((sh) => {
    const s = sh.shareholder;
    if (!s) return;
    const ownershipPercent =
      issued && issued > 0 && sh.numberOfShares != null
        ? Math.round((sh.numberOfShares / issued) * 10000) / 100
        : undefined;
    let owner: StagedOwner;
    if (s.type === 'INSTITUTION') {
      if (
        s.corporationId == null ||
        s.corporation?.incorporationCountry == null
      )
        return;
      owner = {
        kind: 'entity',
        corporationId: s.corporationId,
        companyLegalName:
          s.corporation.companyLegalName ?? sh.shareholderDisplayName ?? '',
        incorporationCountry: s.corporation.incorporationCountry,
        jurisdiction: s.corporation.jurisdiction ?? undefined,
        entityType: s.corporation.corporationType ?? undefined,
      };
    } else {
      if (s.directorId == null || s.nationality == null) return;
      owner = {
        kind: 'individual',
        directorId: s.directorId,
        firstName:
          s.director?.directorFirstName ?? sh.shareholderDisplayName ?? '',
        lastName: s.director?.directorLastName ?? undefined,
        dateOfBirth: s.director?.dateOfBirth ?? undefined,
        nationality: s.nationality,
      };
    }
    const id = generateId('owner');
    entries[id] = {
      owner,
      stakeholdingId: sh.id ?? undefined,
      ownershipPercent,
      governanceRole: sh.governanceRole ?? undefined,
      // A stored GP flag is meaningless in a structure without a GP role.
      isGeneralPartner: gpApplies
        ? (sh.isGeneralPartner ?? undefined)
        : undefined,
    };
    selectedIds.push(id);
  });
  return { selectedIds, entries };
}

// Stakeholding ids that were prefilled from the cap table but are no longer in the
// staged set (the user removed them) — deleted on submit so the cap table matches
// the form. Newly-added owners have no stakeholdingId, so they never appear here.
function computeRemovedStakeholdingIds(
  value: OwnershipFieldValue,
  initial: Set<string>
): string[] {
  const currentIds = new Set(
    value.selectedIds
      .map((id) => value.entries[id]?.stakeholdingId)
      .filter((x): x is string => !!x)
  );
  return Array.from(initial).filter((id) => !currentIds.has(id));
}

// Form Builder "Ownership" node (cap-table module). Variant router + the owner
// table's add/remove flow; owners are staged in `formData` and persisted as one
// reconcile on submit (T5). The Shares/Membership/Partnership structural
// sections, the editable %/units table, and the Ownership Summary are added in
// T3/T4/T6–T8.
// A shareholder added here is not being appointed a director, so the person form
// leaves out what only a directorship needs.
const PERSON_FROM_CAP_TABLE = { personContext: 'shareholder' };

export const OwnershipComponent = ({
  fieldId,
  props,
  validation,
}: OwnershipComponentProps) => {
  const { shareToken } = useApiScope();

  const hasVariant = props?.variant != null;
  const variant: OwnershipVariant = props?.variant ?? 'shares';
  // Partnership: does this structure designate a General Partner? `lp_only`
  // (e.g. Indian LLP) has no GP field and no GP validation; every partner holds
  // the limited/designated role, which `partnerRoleLabel` names per country.
  const partnershipStructure: PartnershipStructure =
    props?.partnershipStructure ?? 'gp_and_lp';
  // Shares and membership have no GP at all, so a prefilled flag is stale there too.
  const gpApplies =
    variant === 'partnership' &&
    structureHasGeneralPartner(partnershipStructure);
  const {
    formData,
    setFormData,
    dslContext,
    registerSubmitHandler,
    isPreview,
    errors,
    setErrors,
    hasAttemptedSubmit,
  } = useContext(FormContext);
  const showAllErrors = hasAttemptedSubmit ?? false;
  const [isModalOpen, setModalOpen] = useState(false);
  // Non-null when the modal is editing an existing owner (vs. adding a new one).
  const [editingId, setEditingId] = useState<string | null>(null);
  const [isPersonDrawerOpen, setPersonDrawerOpen] = useState(false);
  // The person the modal should reopen on, once one has just been created.
  const [preselectPersonId, setPreselectPersonId] = useState<string>();
  // Set while the person drawer completes someone already saved, rather than adding someone.
  const [personDetailsDirectorId, setPersonDetailsDirectorId] =
    useState<number>();

  const entityDetailsKeyId = props?.entityDetailsKeyId;
  const personDetailsKeyId = props?.personDetailsKeyId;
  // A field earlier in the form whose selected people are also the owners here —
  // an LLP's designated partners are its partners, so the cap table follows them
  // instead of asking for the same names twice.
  const partnersFromFieldId = props?.partnersFromFieldId as string | undefined;

  const corporationId = dslContext?.corporationId;
  const companyId = dslContext?.companyId;

  // Same SWR key the modal's person picker reads, so revalidating here is what
  // makes a person created in the drawer selectable the moment it reopens.
  const { mutate: refetchKeyPersons } = useCommendaOSList<
    Record<string, unknown>
  >('keyPerson', companyId ? { companyId } : undefined, 'company.keyPersons');

  // Currency is locked to the entity's incorporation country (ISO code). Primary
  // source is `props.holdingCountry` — the country the author picks in
  // OwnershipEditor (it also derives the holding-type variant), so it's present
  // in both preview and live. Falls back to the live corporation's country when
  // the prop isn't set. No silent USD fallback: an unresolved currency surfaces
  // an error instead of mislabelling the money fields.
  const { data: businessEntity } = useBusinessEntity(corporationId ?? 0);
  const country =
    props?.holdingCountry ??
    (businessEntity?.generalCorporationInfo.incorporationCountry as string | undefined);
  const currency = country
    ? getDefaultCurrency(country) || undefined
    : undefined;
  // Only "resolving" when we have no configured country and are still waiting on
  // the corporation read for the fallback (so we don't flag an error mid-fetch).
  const currencyResolving =
    !props?.holdingCountry &&
    corporationId != null &&
    businessEntity === undefined;

  // Prefill: read the corporation's existing ownership. Empty for a fresh
  // incorporation (the variant sections then use author-configured defaults).
  const { data: ownershipPrefill } = useCommendaOSRead<OwnershipPrefillRead>(
    corporationId != null ? 'corporation.ownership' : null,
    corporationId != null
      ? { companyId: companyId as number, corporationId }
      : null
  );

  const value = readOwnershipValue(fieldId ? formData[fieldId] : undefined);

  // Resolved per-field visibility/editability (author config; both default true).
  const fieldSetting = (key: string): OwnershipFieldVisibility => ({
    visible: props?.fields?.[key]?.visible !== false,
    editable: props?.fields?.[key]?.editable !== false,
  });
  const sharesFieldSettings: OwnershipFieldSettings = {
    authorizedShares: fieldSetting('authorizedShares'),
    esopPercent: fieldSetting('esopPercent'),
    issuedShares: fieldSetting('issuedShares'),
    pricePerShare: fieldSetting('pricePerShare'),
  };
  const membershipFieldSettings: OwnershipFieldSettings = {
    totalShareCapital: fieldSetting('totalShareCapital'),
    totalUnits: fieldSetting('totalUnits'),
  };
  const partnershipFieldSettings: OwnershipFieldSettings = {
    totalShareCapital: fieldSetting('totalShareCapital'),
  };

  // Author defaults seed the Shares config; user edits override them. Defaults
  // are not baked into stored state — only the user's actual entries persist.
  const sharesDefaults: SharesConfig = {
    totalShareCapital: numOrUndef(props?.fields?.totalShareCapital?.default),
    authorizedShares: numOrUndef(props?.fields?.authorizedShares?.default),
    esopEnabled: boolOrUndef(props?.fields?.esopEnabled?.default),
    esopPercent: numOrUndef(props?.fields?.esopPercent?.default),
    issuedShares: numOrUndef(props?.fields?.issuedShares?.default),
    pricePerShare: numOrUndef(props?.fields?.pricePerShare?.default),
  };
  const sharesConfig: SharesConfig = { ...sharesDefaults, ...value.shares };
  const ownerWeights = value.selectedIds.map(
    (id) => value.entries[id]?.ownershipPercent ?? 0
  );
  const shareCapitalEnabled = props?.shareCapitalEnabled ?? false;
  const authorizedSharesDescription =
    props?.copy?.authorizedSharesDescription ||
    AUTHORIZED_SHARES_DESCRIPTION_DEFAULT;
  const computation = computeShares(sharesConfig, ownerWeights, {
    shareCapitalEnabled,
    authorizedVisible: sharesFieldSettings.authorizedShares.visible,
    esopVisible: sharesFieldSettings.esopPercent.visible,
  });

  // Membership config mirrors shares: author defaults seed it, user edits override.
  const membershipDefaults: MembershipConfig = {
    totalShareCapital: numOrUndef(props?.fields?.totalShareCapital?.default),
    hasIssuedUnits: boolOrUndef(props?.fields?.hasIssuedUnits?.default),
    totalUnits: numOrUndef(props?.fields?.totalUnits?.default),
    managerManaged: boolOrUndef(props?.fields?.managerManaged?.default),
  };
  const membershipConfig: MembershipConfig = {
    ...membershipDefaults,
    ...value.membership,
  };
  const membershipComputation = computeMembership(
    membershipConfig,
    ownerWeights
  );

  const partnershipDefaults: PartnershipConfig = {
    totalShareCapital: numOrUndef(props?.fields?.totalShareCapital?.default),
  };
  const partnershipConfig: PartnershipConfig = {
    ...partnershipDefaults,
    ...value.partnership,
  };
  const partnershipComputation = computePartnership(
    partnershipConfig,
    ownerWeights
  );

  // The node's blocking validity (null = valid). Mirrored into the form `errors`
  // map below so the form can't submit while invalid — inline table/section
  // messages give the field-level feedback, this is the submit gate. Presence
  // checks fire when the node is author-marked `required`; the integrity checks
  // (100%, GP, manager, min-capital, issuable ceiling) always fire once owners
  // are staged (they prevent invalid data regardless of the required flag).
  const hasStagedOwners = value.selectedIds.length > 0;
  // Standard per-node "Required" toggle (validation.rules.required). When active,
  // every visible field in the node is required (red dot + blocks submit).
  const required = isRuleActive(validation?.rules?.required, () => true);
  const minCapital = props?.jurisdiction?.minShareCapital;
  const belowMinCapital = (capital: number): string | null =>
    minCapital != null && capital > 0 && capital < minCapital
      ? `Total share capital must be at least ${currency ?? ''} ${minCapital.toLocaleString('en-US')}.`.trim()
      : null;

  // Presence layer: when the node is required, every VISIBLE field must be
  // filled to submit — independent of whether any owner has been added.
  const requiredFieldError: string | null = !required
    ? null
    : variant === 'shares'
      ? sharesFieldSettings.authorizedShares.visible &&
        computation.authorizedShares < 1
        ? 'Authorized shares is required.'
        : computation.issuedShares < 1
          ? 'Issued shares is required.'
          : // Share Capital enabled → price is derived from capital, so require
            // capital; otherwise require the manually-entered price.
            shareCapitalEnabled
            ? !(
                sharesConfig.totalShareCapital &&
                sharesConfig.totalShareCapital >= 1
              )
              ? 'Total share capital is required.'
              : belowMinCapital(computation.effectiveShareCapital)
            : computation.pricePerShare <= 0
              ? 'Price per share is required.'
              : belowMinCapital(computation.effectiveShareCapital)
      : variant === 'membership'
        ? membershipComputation.totalShareCapital < 1
          ? 'Total share capital is required.'
          : membershipComputation.hasIssuedUnits &&
              membershipComputation.totalUnits < 1
            ? 'Total units is required when issuing units.'
            : belowMinCapital(membershipComputation.totalShareCapital)
        : variant === 'partnership'
          ? partnershipComputation.totalShareCapital < 1
            ? 'Total capital contribution is required.'
            : belowMinCapital(partnershipComputation.totalShareCapital)
          : null;

  let nodeValidationError: string | null = requiredFieldError;
  if (!nodeValidationError && hasStagedOwners) {
    if (variant === 'shares') {
      nodeValidationError = !computation.percentBalanced
        ? 'Ownership must total 100%.'
        : computation.authorizedShares < 1
          ? 'Authorized shares is required to save owners.'
          : computation.exceedsMaxIssuable
            ? 'Issued shares exceed the maximum issuable under this structure.'
            : belowMinCapital(computation.effectiveShareCapital);
    } else if (variant === 'membership') {
      const hasManager = value.selectedIds.some(
        (id) => value.entries[id]?.governanceRole === 'MANAGER'
      );
      nodeValidationError = !membershipComputation.percentBalanced
        ? 'Ownership must total 100%.'
        : membershipComputation.totalShareCapital < 1
          ? 'Total share capital is required to save members.'
          : membershipComputation.hasIssuedUnits &&
              membershipComputation.totalUnits < 1
            ? 'Total units is required when issuing units.'
            : membershipComputation.managerManaged && !hasManager
              ? 'A manager-managed company needs at least one Manager.'
              : belowMinCapital(membershipComputation.totalShareCapital);
    } else if (variant === 'partnership') {
      const generalPartnerCount = value.selectedIds.filter(
        (id) => value.entries[id]?.isGeneralPartner
      ).length;
      nodeValidationError = !partnershipComputation.percentBalanced
        ? 'Ownership must total 100%.'
        : partnershipComputation.totalShareCapital < 1
          ? 'Total capital contribution is required to save partners.'
          : value.selectedIds.length < MIN_PARTNERS
            ? MIN_PARTNERS_ERROR
            : gpApplies && generalPartnerCount !== 1
              ? 'Exactly one partner must be designated General Partner.'
              : belowMinCapital(partnershipComputation.totalShareCapital);
    }
    if (
      !nodeValidationError &&
      hasZeroPercentOwner(value.selectedIds, value.entries)
    ) {
      nodeValidationError = zeroPercentOwnerError(variant);
    }
  } else if (!nodeValidationError && !hasStagedOwners) {
    if (
      variant === 'partnership' &&
      partnershipComputation.totalShareCapital >= 1
    ) {
      // Once capital is set, require the minimum partners before submit — and a
      // designated GP too, unless this is a Limited-Partners-only structure.
      nodeValidationError = gpApplies
        ? 'Add at least two partners and designate a General Partner.'
        : 'Add at least two partners.';
    } else if (required && variant !== 'partnership') {
      // The presence layer above only covers the capital fields, so a required
      // node with those filled and nobody on the cap table used to submit — an
      // LLC was filed with no members at all. By here the capital fields already
      // pass, so this cannot fire before the author has entered anything.
      nodeValidationError = noOwnersError(variant);
    }
  }

  // Enabling the ESOP toggle requires a pool-size % (a 0% pool is meaningless) —
  // enforced whenever the toggle is on, independent of the required flag/owners.
  if (
    !nodeValidationError &&
    variant === 'shares' &&
    sharesFieldSettings.esopPercent.visible &&
    sharesConfig.esopEnabled &&
    !(sharesConfig.esopPercent && sharesConfig.esopPercent > 0)
  ) {
    nodeValidationError = 'Equity pool size is required.';
  }

  // An entity owner's file isn't complete until its sub-form has been filled.
  // Presence of `details` is the signal — the sub-form enforces its own
  // required fields before it hands anything back.
  if (
    !nodeValidationError &&
    hasIncompleteEntityDetails(
      value.selectedIds,
      value.entries,
      !!entityDetailsKeyId
    )
  ) {
    nodeValidationError = INCOMPLETE_ENTITY_DETAILS_ERROR;
  }

  if (!currency && !currencyResolving) {
    nodeValidationError =
      'Unable to determine the currency from the entity’s incorporation country.';
  }

  if (!hasVariant) {
    nodeValidationError = 'This entity type isn’t supported for ownership yet.';
  }

  // Persist the resolved currency and country into the value so summaries (incl. the
  // submission review, which only receives the value) can render money fields.
  // Idempotent; only writes when they change.
  useEffect(() => {
    if (!fieldId || !currency) return;
    setFormData((prev) => {
      const cur = readOwnershipValue(prev[fieldId]);
      if (cur.currency === currency && cur.country === country) return prev;
      return { ...prev, [fieldId]: { ...cur, currency, country } };
    });
  }, [fieldId, currency, country, setFormData]);

  // Owners sourced from that field are keyed by the person they came from, so they
  // can be told apart from owners added here and kept in step as the selection
  // changes. Anything added directly — an entity partner, a one-off individual —
  // is never touched.
  useEffect(() => {
    if (!fieldId || !partnersFromFieldId) return;
    const source = formData[partnersFromFieldId] as
      | {
          selectedIds?: string[];
          entries?: Record<string, Record<string, unknown>>;
          [key: string]: unknown;
        }
      | undefined;
    if (!source || typeof source !== 'object') return;

    const selected = source.selectedIds ?? [];
    const labels = (source[PERSON_LABELS_KEY] ?? {}) as Record<string, string>;
    const wanted = new Set(selected.map((personId) => `${SEEDED_OWNER_PREFIX}${personId}`));

    setFormData((prev) => {
      const current = readOwnershipValue(prev[fieldId]);
      const entries = { ...current.entries };
      let changed = false;

      // A person added here by hand already represents them, so the seed must not
      // add a second row for the same director — and drops its own if one appeared.
      const ownedByHand = new Set<number>();
      Object.entries(entries).forEach(([id, entry]) => {
        if (id.startsWith(SEEDED_OWNER_PREFIX)) return;
        const { owner } = entry;
        if (owner.kind === 'individual' && owner.directorId != null) {
          ownedByHand.add(owner.directorId);
        }
      });

      Object.entries(entries).forEach(([id, entry]) => {
        if (!id.startsWith(SEEDED_OWNER_PREFIX)) return;
        const { owner } = entry;
        const duplicate =
          owner.kind === 'individual' &&
          owner.directorId != null &&
          ownedByHand.has(owner.directorId);
        if (wanted.has(id) && !duplicate) return;
        delete entries[id];
        changed = true;
      });

      selected.forEach((personId) => {
        const id = `${SEEDED_OWNER_PREFIX}${personId}`;
        if (entries[id]) return;
        const asDirectorId = Number(personId);
        if (Number.isInteger(asDirectorId) && ownedByHand.has(asDirectorId)) return;
        const authored = source.entries?.[personId] ?? {};
        const label = labels[personId] ?? '';
        const [labelFirst, ...labelRest] = label.split(' ');
        entries[id] = {
          owner: {
            kind: 'individual',
            ...(Number.isInteger(asDirectorId) && asDirectorId > 0
              ? { directorId: asDirectorId }
              : {}),
            firstName: (authored.firstName as string) || labelFirst || label,
            lastName: (authored.lastName as string) || labelRest.join(' ') || undefined,
            dateOfBirth: authored.dateOfBirth as string | undefined,
            nationality: authored.nationality as Country | undefined,
          },
        };
        changed = true;
      });

      if (!changed) return prev;
      return {
        ...prev,
        [fieldId]: {
          ...current,
          entries,
          selectedIds: Object.keys(entries),
        },
      };
    });
  }, [fieldId, partnersFromFieldId, formData, setFormData]);

  // The structure is the node's own configuration, not something resolved from the
  // corporation, so it records itself whether or not a currency ever arrives — a country
  // getDefaultCurrency has no entry for would otherwise leave summaries nothing to read.
  const persistedStructure =
    variant === 'partnership' ? partnershipStructure : undefined;
  useEffect(() => {
    if (!fieldId) return;
    setFormData((prev) => {
      const cur = readOwnershipValue(prev[fieldId]);
      if (cur.partnershipStructure === persistedStructure) return prev;
      return {
        ...prev,
        [fieldId]: { ...cur, partnershipStructure: persistedStructure },
      };
    });
  }, [fieldId, persistedStructure, setFormData]);

  // A structure without a GP role must never carry a GP flag (e.g. a draft or
  // prefill saved when the node still allowed one), or summaries would show it.
  useEffect(() => {
    if (!fieldId || variant !== 'partnership' || gpApplies) return;
    setFormData((prev) => {
      const cur = readOwnershipValue(prev[fieldId]);
      const flagged = Object.keys(cur.entries).filter(
        (id) => cur.entries[id]?.isGeneralPartner
      );
      if (flagged.length === 0) return prev;
      const entries = { ...cur.entries };
      flagged.forEach((id) => {
        entries[id] = { ...entries[id], isGeneralPartner: undefined };
      });
      return { ...prev, [fieldId]: { ...cur, entries } };
    });
  }, [fieldId, variant, gpApplies, value.entries, setFormData]);

  const patchShares = (patch: Partial<SharesConfig>) => {
    if (!fieldId) return;
    setFormData((prev) => {
      const current = readOwnershipValue(prev[fieldId]);
      return {
        ...prev,
        [fieldId]: { ...current, shares: { ...current.shares, ...patch } },
      };
    });
  };

  const patchMembership = (patch: Partial<MembershipConfig>) => {
    if (!fieldId) return;
    setFormData((prev) => {
      const current = readOwnershipValue(prev[fieldId]);
      // Turning manager-managed off demotes every Manager back to Member, so no
      // stale Manager roles linger (and re-enabling starts clean).
      let { entries } = current;
      if (patch.managerManaged === false) {
        entries = { ...current.entries };
        Object.keys(entries).forEach((key) => {
          entries[key] = { ...entries[key], governanceRole: undefined };
        });
      }
      return {
        ...prev,
        [fieldId]: {
          ...current,
          entries,
          membership: { ...current.membership, ...patch },
        },
      };
    });
  };

  const patchPartnership = (patch: Partial<PartnershipConfig>) => {
    if (!fieldId) return;
    setFormData((prev) => {
      const current = readOwnershipValue(prev[fieldId]);
      return {
        ...prev,
        [fieldId]: {
          ...current,
          partnership: { ...current.partnership, ...patch },
        },
      };
    });
  };

  const addOwner = (
    owner: StagedOwner,
    opts?: { isGeneralPartner?: boolean; governanceRole?: GovernanceRole },
    details?: Record<string, unknown>
  ) => {
    if (!fieldId) return;
    const id = generateId('owner');
    setFormData((prev) => {
      const current = readOwnershipValue(prev[fieldId]);
      const entries = { ...current.entries };
      // Partnership: exactly one GP — designating a new GP demotes the rest.
      if (opts?.isGeneralPartner) {
        Object.keys(entries).forEach((key) => {
          entries[key] = { ...entries[key], isGeneralPartner: false };
        });
      }
      entries[id] = {
        owner,
        isGeneralPartner: opts?.isGeneralPartner || undefined,
        governanceRole: opts?.governanceRole,
        details,
      };
      return {
        ...prev,
        [fieldId]: {
          ...current,
          selectedIds: [...current.selectedIds, id],
          entries,
        },
      };
    });
  };

  // Edit an existing owner's identity in place — its ownershipPercent (edited
  // inline in the table) is preserved.
  const updateOwner = (
    id: string,
    owner: StagedOwner,
    opts?: { isGeneralPartner?: boolean; governanceRole?: GovernanceRole },
    details?: Record<string, unknown>
  ) => {
    if (!fieldId) return;
    setFormData((prev) => {
      const current = readOwnershipValue(prev[fieldId]);
      const existing = current.entries[id];
      if (!existing) return prev;
      const entries = { ...current.entries };
      // Partnership: exactly one GP — designating this one demotes the rest.
      if (opts?.isGeneralPartner) {
        Object.keys(entries).forEach((key) => {
          entries[key] = { ...entries[key], isGeneralPartner: false };
        });
      }
      entries[id] = {
        ...existing,
        owner,
        isGeneralPartner: opts?.isGeneralPartner || undefined,
        governanceRole: opts?.governanceRole,
        details: details ?? existing.details,
      };
      return { ...prev, [fieldId]: { ...current, entries } };
    });
  };

  const removeOwner = (id: string) => {
    if (!fieldId) return;
    setFormData((prev) => {
      const current = readOwnershipValue(prev[fieldId]);
      const entries = { ...current.entries };
      delete entries[id];
      return {
        ...prev,
        [fieldId]: {
          ...current,
          selectedIds: current.selectedIds.filter((s) => s !== id),
          entries,
        },
      };
    });
  };

  // Records the corporation a reconcile minted for an entity owner, so a retry
  // links that corporation instead of minting a second one for the same entity.
  const linkCreatedEntity = useCallback(
    (entryId: string, createdCorporationId: number) => {
      if (!fieldId) return;
      setFormData((prev) => {
        const current = readOwnershipValue(prev[fieldId]);
        const entry = current.entries[entryId];
        if (!entry || entry.owner.kind !== 'entity' || entry.owner.corporationId != null)
          return prev;
        return {
          ...prev,
          [fieldId]: {
            ...current,
            entries: {
              ...current.entries,
              [entryId]: {
                ...entry,
                owner: { ...entry.owner, corporationId: createdCorporationId },
              },
            },
          },
        };
      });
    },
    [fieldId, setFormData]
  );

  const updateOwnerPercent = (id: string, percent: number | undefined) => {
    if (!fieldId) return;
    setFormData((prev) => {
      const current = readOwnershipValue(prev[fieldId]);
      const entry = current.entries[id];
      if (!entry) return prev;
      return {
        ...prev,
        [fieldId]: {
          ...current,
          entries: {
            ...current.entries,
            [id]: { ...entry, ownershipPercent: percent },
          },
        },
      };
    });
  };

  // Seed the Shares config from an existing cap table once (don't clobber user
  // edits). Owner rows aren't prefilled — the read lacks owner nationality/country.
  const prefillSeededRef = useRef(false);
  useEffect(() => {
    if (prefillSeededRef.current || !fieldId || variant !== 'shares') return;
    const cls = ownershipPrefill?.shareClasses?.[0];
    if (!cls) return;
    prefillSeededRef.current = true;
    setFormData((prev) => {
      const cur = readOwnershipValue(prev[fieldId]);
      if (cur.shares !== undefined) return prev;
      return {
        ...prev,
        [fieldId]: {
          ...cur,
          shares: {
            authorizedShares: cls.authorizedShares ?? undefined,
            issuedShares: cls.outstandingShares ?? undefined,
            pricePerShare: cls.pricePerShareAmount ?? undefined,
          },
        },
      };
    });
  }, [ownershipPrefill, fieldId, variant, setFormData]);

  // Persist staged owners on form submit (Shares variant). The node value maps
  // to many shareholdings, so it can't use the standard single-field writer; it
  // registers a submit handler that runs after the standard COS writes.
  useEffect(() => {
    if (!fieldId || variant !== 'shares' || isPreview) return;
    const cId = companyId;
    const corpId = corporationId;
    if (cId == null || corpId == null) return;
    registerSubmitHandler?.(fieldId, () =>
      reconcileShareholdings({
        shareToken,
        value,
        computation,
        companyId: cId,
        corporationId: corpId,
        currency,
        removedStakeholdingIds: computeRemovedStakeholdingIds(
          value,
          initialStakeholdingIdsRef.current
        ),
        onEntityCreated: linkCreatedEntity,
      })
    );
  }, [
    registerSubmitHandler,
    fieldId,
    variant,
    isPreview,
    value,
    computation,
    companyId,
    corporationId,
    currency,
    linkCreatedEntity,
  ]);

  // Seed the Membership config from an existing cap table once (don't clobber
  // user edits). Units live in the class's authorized count; capital = units × price.
  const membershipPrefillSeededRef = useRef(false);
  useEffect(() => {
    if (
      membershipPrefillSeededRef.current ||
      !fieldId ||
      variant !== 'membership'
    )
      return;
    const cls = ownershipPrefill?.shareClasses?.[0];
    if (!cls) return;
    membershipPrefillSeededRef.current = true;
    setFormData((prev) => {
      const cur = readOwnershipValue(prev[fieldId]);
      if (cur.membership !== undefined) return prev;
      const hasIssuedUnits = cls.hasIssuedUnits ?? false;
      const units = cls.authorizedShares ?? undefined;
      const price = cls.pricePerShareAmount ?? undefined;
      // Units off ⇒ price = 1, so capital = units. Units on ⇒ capital = units × price.
      const totalShareCapital =
        hasIssuedUnits && units != null && price != null
          ? units * price
          : units;
      return {
        ...prev,
        [fieldId]: {
          ...cur,
          membership: {
            totalShareCapital: totalShareCapital ?? undefined,
            hasIssuedUnits,
            totalUnits: hasIssuedUnits ? units : undefined,
          },
        },
      };
    });
  }, [ownershipPrefill, fieldId, variant, setFormData]);

  // Persist staged members on form submit (Membership variant) — same submit-
  // handler registry as Shares; runs after the standard COS writes.
  useEffect(() => {
    if (!fieldId || variant !== 'membership' || isPreview) return;
    const cId = companyId;
    const corpId = corporationId;
    if (cId == null || corpId == null) return;
    registerSubmitHandler?.(fieldId, () =>
      reconcileMembership({
        shareToken,
        value,
        computation: membershipComputation,
        companyId: cId,
        corporationId: corpId,
        currency,
        removedStakeholdingIds: computeRemovedStakeholdingIds(
          value,
          initialStakeholdingIdsRef.current
        ),
        onEntityCreated: linkCreatedEntity,
      })
    );
  }, [
    registerSubmitHandler,
    fieldId,
    variant,
    isPreview,
    value,
    membershipComputation,
    companyId,
    corporationId,
    currency,
    linkCreatedEntity,
  ]);

  // Seed the Partnership config from an existing cap table once. Price is 1, so
  // total capital = the class's authorized count.
  const partnershipPrefillSeededRef = useRef(false);
  useEffect(() => {
    if (
      partnershipPrefillSeededRef.current ||
      !fieldId ||
      variant !== 'partnership'
    )
      return;
    const cls = ownershipPrefill?.shareClasses?.[0];
    if (!cls) return;
    partnershipPrefillSeededRef.current = true;
    setFormData((prev) => {
      const cur = readOwnershipValue(prev[fieldId]);
      if (cur.partnership !== undefined) return prev;
      return {
        ...prev,
        [fieldId]: {
          ...cur,
          partnership: { totalShareCapital: cls.authorizedShares ?? undefined },
        },
      };
    });
  }, [ownershipPrefill, fieldId, variant, setFormData]);

  // Persist staged partners on form submit (Partnership variant).
  useEffect(() => {
    if (!fieldId || variant !== 'partnership' || isPreview) return;
    const cId = companyId;
    const corpId = corporationId;
    if (cId == null || corpId == null) return;
    registerSubmitHandler?.(fieldId, () =>
      reconcilePartnership({
        shareToken,
        value,
        computation: partnershipComputation,
        companyId: cId,
        corporationId: corpId,
        currency,
        removedStakeholdingIds: computeRemovedStakeholdingIds(
          value,
          initialStakeholdingIdsRef.current
        ),
        onEntityCreated: linkCreatedEntity,
      })
    );
  }, [
    registerSubmitHandler,
    fieldId,
    variant,
    isPreview,
    value,
    partnershipComputation,
    companyId,
    corporationId,
    currency,
    linkCreatedEntity,
  ]);

  // Prefill owner rows from the entity's existing shareholdings (variant-agnostic).
  // Create-only reconcile upserts by (stakeholder, corp), so re-submitting an
  // unchanged prefilled owner is idempotent; removing one isn't persisted yet.
  const ownersPrefillSeededRef = useRef(false);
  // Stakeholding ids present at prefill, diffed against the staged set on submit
  // to delete owners the user removed (reconcile).
  const initialStakeholdingIdsRef = useRef<Set<string>>(new Set());
  useEffect(() => {
    if (ownersPrefillSeededRef.current || !fieldId) return;
    const shareholdings = ownershipPrefill?.shareholdings;
    if (!shareholdings || shareholdings.length === 0) return;
    ownersPrefillSeededRef.current = true;
    const issued =
      ownershipPrefill?.shareClasses?.[0]?.outstandingShares ?? undefined;
    const { selectedIds, entries } = mapPrefilledOwners(
      shareholdings,
      issued,
      gpApplies
    );
    if (selectedIds.length === 0) return;
    setFormData((prev) => {
      const cur = readOwnershipValue(prev[fieldId]);
      if (cur.selectedIds.length > 0) return prev;
      initialStakeholdingIdsRef.current = new Set(
        selectedIds
          .map((id) => entries[id]?.stakeholdingId)
          .filter((x): x is string => !!x)
      );
      return { ...prev, [fieldId]: { ...cur, selectedIds, entries } };
    });
  }, [ownershipPrefill, fieldId, gpApplies, setFormData]);

  // Mirror the node's validity into the form errors map so submission is gated
  // (validateAllSteps blocks submit on any non-empty error). Keyed by fieldId;
  // cleared once the node is valid. Persists if the node unmounts while invalid,
  // so a final submit still bounces back to this step.
  useEffect(() => {
    if (!fieldId) return;
    const current = errors[fieldId];
    if (nodeValidationError) {
      if (current !== nodeValidationError)
        setErrors({ ...errors, [fieldId]: nodeValidationError });
    } else if (current !== undefined) {
      const { [fieldId]: _removed, ...rest } = errors;
      setErrors(rest);
    }
  }, [fieldId, nodeValidationError, errors, setErrors]);

  return (
    <div
      data-testid={`ownership-node-${variant}`}
      data-field-id={fieldId}
      className="flex flex-col gap-8"
    >
      {!hasVariant ? (
        <InputErrorMessage message="This entity type doesn’t have a supported ownership structure yet. Pick a different country, jurisdiction, or entity type in the form settings." />
      ) : !currency ? (
        <InputErrorMessage message="We couldn’t determine the currency from this entity’s incorporation country, so ownership amounts can’t be shown. Set the entity’s country to continue." />
      ) : variant === 'shares' ? (
        <>
          <SharesSections
            config={sharesConfig}
            onPatch={patchShares}
            computation={computation}
            currency={currency}
            jurisdiction={props?.jurisdiction}
            shareCapitalEnabled={shareCapitalEnabled}
            fieldSettings={sharesFieldSettings}
            hasOwners={value.selectedIds.length > 0}
            required={required}
            showErrors={showAllErrors}
            authorizedSharesDescription={authorizedSharesDescription}
          />
          <SharesTable
            ids={value.selectedIds}
            entries={value.entries}
            percentTotal={computation.percentTotal}
            percentBalanced={computation.percentBalanced}
            onAddOwner={() => {
              setEditingId(null);
              setModalOpen(true);
            }}
            onEditOwner={(id) => {
              setEditingId(id);
              setModalOpen(true);
            }}
            onRemoveOwner={removeOwner}
            collectsEntityDetails={!!entityDetailsKeyId}
            onUpdatePercent={updateOwnerPercent}
          />
          <SharesSummary
            ids={value.selectedIds}
            entries={value.entries}
            computation={computation}
            currency={currency}
          />
        </>
      ) : variant === 'membership' ? (
        <>
          <MembershipSections
            config={membershipConfig}
            onPatch={patchMembership}
            currency={currency}
            jurisdiction={props?.jurisdiction}
            fieldSettings={membershipFieldSettings}
            hasOwners={value.selectedIds.length > 0}
            required={required}
            showErrors={showAllErrors}
          />
          <MembershipTable
            ids={value.selectedIds}
            entries={value.entries}
            managerManaged={membershipComputation.managerManaged}
            percentTotal={membershipComputation.percentTotal}
            percentBalanced={membershipComputation.percentBalanced}
            onAddOwner={() => {
              setEditingId(null);
              setModalOpen(true);
            }}
            onEditOwner={(id) => {
              setEditingId(id);
              setModalOpen(true);
            }}
            onRemoveOwner={removeOwner}
            collectsEntityDetails={!!entityDetailsKeyId}
            onUpdatePercent={updateOwnerPercent}
          />
          <MembershipSummary
            ids={value.selectedIds}
            entries={value.entries}
            computation={membershipComputation}
            currency={currency}
          />
        </>
      ) : (
        <>
          <PartnershipSections
            config={partnershipConfig}
            onPatch={patchPartnership}
            currency={currency}
            jurisdiction={props?.jurisdiction}
            hasOwners={value.selectedIds.length > 0}
            fieldSettings={partnershipFieldSettings}
            required={required}
            showErrors={showAllErrors}
          />
          <PartnershipTable
            ids={value.selectedIds}
            entries={value.entries}
            percentTotal={partnershipComputation.percentTotal}
            percentBalanced={partnershipComputation.percentBalanced}
            gpApplies={gpApplies}
            hasGeneralPartner={
              value.selectedIds.filter(
                (id) => value.entries[id]?.isGeneralPartner
              ).length === 1
            }
            onAddOwner={() => {
              setEditingId(null);
              setModalOpen(true);
            }}
            onEditOwner={(id) => {
              setEditingId(id);
              setModalOpen(true);
            }}
            onRemoveOwner={removeOwner}
            collectsEntityDetails={!!entityDetailsKeyId}
            onUpdatePercent={updateOwnerPercent}
          />
          <PartnershipSummary
            ids={value.selectedIds}
            entries={value.entries}
            computation={partnershipComputation}
            currency={currency}
            gpApplies={gpApplies}
            country={country}
          />
        </>
      )}

      <AddOwnerModal
        key={editingId ?? preselectPersonId ?? 'add-owner'}
        isOpen={isModalOpen}
        onClose={() => {
          setModalOpen(false);
          setEditingId(null);
          setPreselectPersonId(undefined);
        }}
        variant={variant}
        gpApplies={gpApplies}
        companyId={companyId}
        corporationId={corporationId}
        initialOwner={editingId ? value.entries[editingId]?.owner : undefined}
        initialIsGeneralPartner={
          editingId ? value.entries[editingId]?.isGeneralPartner : undefined
        }
        managerManaged={membershipComputation.managerManaged}
        initialGovernanceRole={
          editingId ? value.entries[editingId]?.governanceRole : undefined
        }
        preselectPersonId={preselectPersonId}
        entityDetailsKeyId={entityDetailsKeyId}
        initialDetails={
          editingId ? value.entries[editingId]?.details : undefined
        }
        personDetailsConfig={
          personDetailsKeyId
            ? {
                keyId: personDetailsKeyId,
                defaultValues: PERSON_FROM_CAP_TABLE,
                onRequestDetails: (directorId) => {
                  setModalOpen(false);
                  setPersonDetailsDirectorId(directorId);
                  setPersonDrawerOpen(true);
                },
              }
            : undefined
        }
        onRequestNewPerson={
          personDetailsKeyId
            ? () => {
                setModalOpen(false);
                setPersonDrawerOpen(true);
              }
            : undefined
        }
        onStaged={(owner, opts, details) => {
          if (editingId) {
            updateOwner(editingId, owner, opts, details);
          } else {
            addOwner(owner, opts, details);
          }
          setModalOpen(false);
          setEditingId(null);
          setPreselectPersonId(undefined);
        }}
      />

      <OwnerSubFormDrawer
        isOpen={isPersonDrawerOpen}
        onClose={() => {
          setPersonDrawerOpen(false);
          setPersonDetailsDirectorId(undefined);
          setModalOpen(true);
        }}
        keyId={personDetailsKeyId}
        title={
          personDetailsDirectorId != null ? 'Complete details' : 'Add a person'
        }
        defaultValues={PERSON_FROM_CAP_TABLE}
        omitDirectorId={personDetailsDirectorId == null}
        directorId={personDetailsDirectorId}
        missingTemplateHint="Link a form template in the builder to define what to collect for a new person."
        onSubmit={async (data) => {
          // The sub-form writes the person through Commenda OS and hands back
          // the new id; without one nothing was created, so the picker reopens
          // empty rather than pointing at a record that doesn't exist.
          const createdId = data.__createdDirectorId;
          const personId =
            typeof createdId === 'number' ? createdId : personDetailsDirectorId;
          setPersonDrawerOpen(false);
          setPersonDetailsDirectorId(undefined);
          await refetchKeyPersons();
          setPreselectPersonId(
            personId != null ? String(personId) : undefined
          );
          setModalOpen(true);
        }}
      />
    </div>
  );
};
